import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

interface DriverDetails {
  driverName: string;
  mobileNumber: string;
  isPrimary: number;
}

export interface OriginalDataItem {
  deviceID: string;
  deviceType: string;
  latitude: number;
  longitude: number;
  vehicleDashboardVehicleDetails: any;
  address: any;
  liveSpeed: number;
  networkStatus: string;
  todayDistance: number;
  totalDistance: number;
  driverName: string;
  contactNumber: number;
  vehicleLiveStatus: string;
  drivers?: DriverDetails[];
}

interface RadiusData {
  rtoNumber: string;
  deviceType: string;
  distance: string;
  status: string;
  latitude: number;
  longitude: number;
  speed: string;
  address: any;
  totalDistance: number;
  todayDistance: number;
  driverName: string;
  contactNumber: number;
  networkStatus: string;
  vehicleLiveStatus: string;
}

// vehicle status
interface DashboardVehiclesStatusArgs {
  arg1: string;
  arg2: boolean;
  signal?: any;
}

const initialState: any = {
  isLoading: false,
  data: [],
  currentState: [],
  error: null
};

// dashboard
export const dashboardAction = createAsyncThunk<AxiosResponse<any>, boolean>(
  'user/dashboard',
  async (arg, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DASHBOARD_VEHICLES}`
      });
      const changeData = response?.data?.data[0]?.liveDeviceDetails;
      return changeData;
    } catch (error: any) {
      if (arg)
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error.response.data?.message,
            severity: 'error'
          })
        );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// vehicle status count
export const dashboardVehiclesCountAction = createAsyncThunk<AxiosResponse<any>, boolean>(
  'user/dashboard/vehicles/count',
  async (arg, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DASHBOARD_VEHICLES_COUNT}`
      });
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// today trip details
export const tripTodayAction = createAsyncThunk<AxiosResponse<any>>(
  'today/trips',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.TODAY_TRIP_DETAILS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const dashboardVehiclesStatusAction = createAsyncThunk<
  AxiosResponse<any>,
  DashboardVehiclesStatusArgs
>('user/dashboard/vehicles/status', async ({ arg1, arg2, signal }, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${URLs.DASHBOARD_VEHICLES_STATUS}${arg1}`,
      signal
    });

    if (response?.data?.data?.length === 0 && arg2) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'No Vehicles found',
          severity: 'warning'
        })
      );
    }
    return response?.data?.data;
  } catch (error: any) {
    if (arg1) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.message,
          severity: 'error'
        })
      );
    }
    return thunkApi.rejectWithValue(error);
  }
});

// radius and location
export const dashboardRadiusAction = createAsyncThunk<
  AxiosResponse<any>,
  { lat: number; lng: number; value: number },
  { rejectValue: any }
>('user/dashboard/vehicles/radius', async (args, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${URLs.DASHBOARD_LOCATION_RADIUS}?latitude=${args.lat}&longitude=${args.lng}&radius=${args.value}`
    });
    if (response?.data?.message === 'No data found') {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'No Vehicle found',
          severity: 'warning'
        })
      );
    }
    return response?.data?.data;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error.response.data?.message,
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error);
  }
});

// dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(dashboardAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(dashboardAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        let isEqual = true;

        for (let i = 0; i < current(state).data.length; i++) {
          if (
            JSON.stringify(current(state).data[i]) !== JSON.stringify(action.payload[i])
          ) {
            isEqual = false;
            break;
          }
        }

        if (isEqual || current(state).data.length !== action.payload.length) {
          state.data = action.payload?.map((item: OriginalDataItem) => {
            return {
              id: item?.deviceID,
              type: item?.deviceType,
              lat: item?.latitude,
              lng: item?.longitude,
              status: item?.networkStatus,
              info: {
                speed: `${item?.liveSpeed} kmph`,
                todayDistance: `${item?.todayDistance?.toFixed(2)} KM`,
                totalDistance: `${item?.totalDistance?.toFixed(2)} KM`,
                driverName: item?.driverName,
                driverContactNo: item?.contactNumber
              }
            };
          });
        }
        state.error = null;
      })
      .addCase(dashboardAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// dashboard vehicle count slice
const dashboardVehiclesCountSlice = createSlice({
  name: 'dashboard vehicles count',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(dashboardVehiclesCountAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(dashboardVehiclesCountAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(dashboardVehiclesCountAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// dashboard vehicle status slice
const dashboardVehiclesStatusSlice = createSlice({
  name: 'dashboard/vehicleStatus',
  initialState,
  reducers: {
    clearLive: state => {
      state.data = [];
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(dashboardVehiclesStatusAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(dashboardVehiclesStatusAction.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        let isEqual = true;

        for (let i = 0; i < current(state).data.length; i++) {
          if (
            JSON.stringify(current(state).data[i]) !== JSON.stringify(action.payload[i])
          ) {
            isEqual = false;
            break;
          }
        }
        if (isEqual || current(state).data.length !== action.payload.length) {
          state.data = action.payload.map((item: OriginalDataItem) => {
            return {
              id: item?.deviceID,
              type: item?.vehicleDashboardVehicleDetails?.vehicleType,
              lat: item?.latitude,
              lng: item?.longitude,
              status: item?.vehicleDashboardVehicleDetails?.vehicleLiveStatus,
              updatedAt: item?.vehicleDashboardVehicleDetails?.lastUpdatedTime,
              info: {
                speed: `${item?.vehicleDashboardVehicleDetails?.speed} kmph`,
                todayDistance: `${item?.vehicleDashboardVehicleDetails?.todayDistance?.toFixed(
                  2
                )} KM`,
                totalDistance: `${item?.vehicleDashboardVehicleDetails?.totalDistance?.toFixed(
                  2
                )} KM`,
                driverName: item?.driverName,
                driverContactNo: item?.contactNumber
              }
            };
          });
        }
        let oldVehicles = current(state)?.data?.map((item: any) => item?.id);
        let newVehicles = action.payload?.map((item: any) => item?.deviceID);
        if (JSON.stringify(oldVehicles) !== JSON.stringify(newVehicles)) {
          state.data = action.payload.map((item: OriginalDataItem) => {
            return {
              id: item?.deviceID,
              type: item?.vehicleDashboardVehicleDetails?.vehicleType,
              lat: item?.latitude,
              lng: item?.longitude,
              status: item?.vehicleDashboardVehicleDetails?.vehicleLiveStatus,
              updatedAt: item?.vehicleDashboardVehicleDetails?.lastUpdatedTime,
              info: {
                speed: `${item?.vehicleDashboardVehicleDetails?.speed} kmph`,
                todayDistance: `${item?.vehicleDashboardVehicleDetails?.todayDistance?.toFixed(
                  2
                )} KM`,
                totalDistance: `${item?.vehicleDashboardVehicleDetails?.totalDistance?.toFixed(
                  2
                )} KM`,
                ...(item?.vehicleDashboardVehicleDetails?.vehicleLiveStatus ===
                  'moving' && {
                  driverName:
                    item?.drivers?.[0]?.driverName || item?.drivers?.[1]?.driverName,
                  driverContactNo:
                    item?.drivers?.[0]?.mobileNumber || item?.drivers?.[1]?.mobileNumber
                })
              }
            };
          });
        }
        state.error = null;
      })
      .addCase(dashboardVehiclesStatusAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// dashboard radius and location slice
const dashboardRadiusSlice = createSlice({
  name: 'dashboard vehicles status radius',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(dashboardRadiusAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(dashboardRadiusAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.map((item: RadiusData) => {
          return {
            id: item.rtoNumber,
            name: item.deviceType,
            distance: item.distance,
            status: item.status,
            lat: item.latitude,
            lng: item.longitude,
            info: {
              speed: `${item?.speed} kmph`,
              vehicleNumber: item?.rtoNumber?.toUpperCase(),
              status: item?.networkStatus,
              todayDistance: `${item?.todayDistance?.toFixed(3)} KM`,
              totalDistance: `${item?.totalDistance?.toFixed(3)} KM`,
              vehicleStatus: item.status,
              driverName: item?.driverName,
              driverContactNo: item?.contactNumber,
              lat: item.latitude,
              lng: item.longitude
            }
          };
        });
        state.error = null;
      })
      .addCase(dashboardRadiusAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

export const mapVehiclesStatusAction = createAsyncThunk<
  AxiosResponse<any>,
  DashboardVehiclesStatusArgs
>('vehicles/status', async ({ arg1, arg2, signal }, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${URLs.DASHBOARD_VEHICLES_STATUS}${arg1}`,
      signal: signal
    });
    if (response?.data?.message === 'No data found' && arg2) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'No Vehicle found',
          severity: 'warning'
        })
      );
    }
    return response?.data?.data;
  } catch (error: any) {
    if (error?.message !== 'canceled') {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
    }
    return thunkApi.rejectWithValue(error);
  }
});

//reducer for maplive status
const mapVehiclesStatusSlice = createSlice({
  name: 'map/vehicleStatus',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(mapVehiclesStatusAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mapVehiclesStatusAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        let isEqual = true;

        for (let i = 0; i < current(state).data.length; i++) {
          if (
            JSON.stringify(current(state).data[i]) !== JSON.stringify(action.payload[i])
          ) {
            isEqual = false;
            break;
          }
        }

        if (isEqual || current(state).data.length !== action.payload.length) {
          state.data = action.payload.map((item: OriginalDataItem) => {
            return {
              id: item?.deviceID,
              type: item?.vehicleDashboardVehicleDetails?.vehicleType,
              lat: item?.latitude,
              lng: item?.longitude,
              status: item?.vehicleDashboardVehicleDetails?.vehicleLiveStatus,
              info: {
                speed: `${item?.vehicleDashboardVehicleDetails?.speed} kmph`,
                todayDistance: `${item?.vehicleDashboardVehicleDetails?.todayDistance?.toFixed(
                  2
                )} KM`,
                totalDistance: `${item?.vehicleDashboardVehicleDetails?.totalDistance?.toFixed(
                  2
                )} KM`,
                driverName: item?.driverName,
                driverContactNo: item?.contactNumber
              }
            };
          });
        }
        state.error = null;
      })
      .addCase(mapVehiclesStatusAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// my todaytrips details slice
const todayMytripsSlice = createSlice({
  name: 'todaytrips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(tripTodayAction.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.error = null;
      })
      .addCase(tripTodayAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action?.payload?.data?.map((item: any, index: number) => ({
          ...item,
          id: index + 1
        }));
        state.error = null;
      })
      .addCase(tripTodayAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//action for trips vehicle data
export const tripVehicleAction = createAsyncThunk<any, any>(
  'api/filtervehicle',
  async (data, thunkApi) => {
    const { vehicle, signal } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.TRIP_VEHICLE_FILTER}?vehicleNumber=${vehicle}`,
        signal
      });
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

//reducers for trips vehicle filter
const tripVehicleSlice = createSlice({
  name: 'filtervehicle',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(tripVehicleAction.pending, (state, action) => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(tripVehicleAction.fulfilled, (state, action: any) => {
      state.isLoading = false;
      const {
        data: { data }
      } = action.payload;
      const oldVehicle = current(state).currentState;
      state.currentState = data;
      if (JSON.stringify(oldVehicle) !== JSON.stringify(data)) {
        state.data = {
          id: data?.deviceID,
          type: data?.vehicleDashboardVehicleDetails?.vehicleType,
          lat: data?.latitude,
          lng: data?.longitude,
          status: data?.vehicleDashboardVehicleDetails?.vehicleLiveStatus,
          info: {
            speed: `${data?.vehicleDashboardVehicleDetails?.speed} kmph`,
            todayDistance: `${data?.vehicleDashboardVehicleDetails?.todayDistance?.toFixed(
              2
            )} KM`,
            totalDistance: `${data?.vehicleDashboardVehicleDetails?.totalDistance?.toFixed(
              2
            )} KM`,
            driverName: data?.driverName,
            driverContactNo: data?.contactNumber
          }
        };
      }
      state.error = null;
    });
    builder.addCase(tripVehicleAction.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.message;
    });
  }
});

export const dashboard = dashboardSlice.reducer;
export const dashboardVehiclesCount = dashboardVehiclesCountSlice.reducer;
export const dashboardVehiclesStatus = dashboardVehiclesStatusSlice.reducer;
export const dashboardRadius = dashboardRadiusSlice.reducer;
export const mapVehicleStatus = mapVehiclesStatusSlice.reducer;
export const tripTodaySlice = todayMytripsSlice.reducer;
export const { clearLive } = dashboardVehiclesStatusSlice.actions;
export const tripVehicleData = tripVehicleSlice.reducer;
